import ClearIcon from '@mui/icons-material/Clear';
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import { Button, Container, FormControlLabel, Modal, Radio, RadioGroup, Stack, TextField, Typography } from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ActivityStatus, saveActivity } from '../../redux/reducers/activityReducer';
import { selectMultipleTitles } from '../../redux/reducers/catalogsReducer';
import { selectFrameworkTitle } from '../../redux/reducers/frameworkReducer';
import LoadingIndicator from '../LoadingIndicator';

dayjs.extend(advancedFormat);

function ControlTitles({ controlIds }) {
  const controlTitles = useSelector((state) => selectMultipleTitles(state, controlIds));
  return <Typography variant="regular">{controlTitles}</Typography>;
}

function FrameworkTitle({ frameworkId }) {
  const frameworkTitle = useSelector((state) => selectFrameworkTitle(state, frameworkId));
  return <Typography variant="regular">{frameworkTitle}</Typography>;
}

function UpdateActivityModal({ handleClose, handleOpen, activity, modalList }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [activityDetails, setActivityDetails] = useState(activity);

  const handleChange = (event) => {
    const { name, value } = event.target;
    const newDetails = { ...activityDetails };
    newDetails[name] = value;
    setActivityDetails(newDetails);
    setUnsavedChanges(true);
  };

  const handleSave = () => {
    if (unsavedChanges) {
      dispatch(saveActivity(activityDetails));
    }
    handleClose();
  };

  const handleCancel = () => {
    const newState = { ...activity };
    setActivityDetails(newState);
    handleClose();
  };

  useEffect(() => {
    if (activity.id) {
      const newState = { ...activity };
      setActivityDetails(newState);
    }
  }, [activity]);

  return (
    <Modal open onClose={() => handleClose()}>
      {activityDetails.id ? (
        <Container variant="modal" disableGutters>
          <Stack variant="modalTop" spacing={2}>
            <Typography variant="h6">{t('activity.modal.updateActivity')}</Typography>
            <Button
              variant="outlined"
              startIcon={<ModeEditOutlinedIcon />}
              onClick={() => handleOpen(modalList.EDIT)}
            >
              {t('activity.modal.editActivity')}
            </Button>
            <Stack>
              <Typography>{t('activity.modal.title')}</Typography>
              <Typography variant="body2">{activity.title}</Typography>
            </Stack>
            <Stack>
              <Typography>{t('activity.modal.framework')}</Typography>
              <Typography variant="body2">
                {activity.id && <FrameworkTitle frameworkId={activity.frameworkId} />}
              </Typography>
            </Stack>
            <Stack spacing={3}>
              <Stack>
                <Typography sx={{ ml: '16px' }}>{t('activity.modal.controls')}</Typography>
                <Typography variant="body2" sx={{ ml: '16px' }}>
                  {activity.id && <ControlTitles controlIds={activity.controlIds} />}
                </Typography>
              </Stack>
              <Stack>
                <Typography>{t('activity.modal.status')}</Typography>
                <RadioGroup row name="activityStatus" value={activityDetails.activityStatus} onChange={handleChange}>
                  {ActivityStatus.list().map((status) => (
                    <FormControlLabel
                      key={status}
                      value={status}
                      control={<Radio variant="radio" />}
                      label={t(`activity.status.${status}`)}
                    />
                  ))}
                </RadioGroup>
              </Stack>
              <TextField
                variant="outlined"
                subtype="signinField"
                onChange={handleChange}
                name="notes"
                value={activityDetails.notes}
                multiline
                minRows="4"
                InputLabelProps={{
                  shrink: true,
                }}
                label={t('activity.modal.notes')}
                required
              />
            </Stack>
          </Stack>
          <Stack variant="modalButtons" direction="row" justifyContent="flex-end" spacing={1}>
            <Button variant="text" startIcon={<ClearIcon />} onClick={() => handleCancel()}>
              {t('activity.modal.cancel')}
            </Button>
            <Button variant="contained" startIcon={<SaveRoundedIcon />} onClick={() => handleSave()}>
              {t('activity.modal.updateActivity')}
            </Button>
          </Stack>
        </Container>
      ) : (
        <LoadingIndicator />
      )}
    </Modal>
  );
}

export default UpdateActivityModal;
